import React, { useEffect, useState, memo, useRef, useCallback } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useApi } from '../../../../../shared/hooks/useApi';
import CustomTable from '../../../../../shared/components/table/custom-table';
import { ActionsMenu } from '../../../../../shared/components/table/custom-actions';
import { LoaderComponent } from '../../../../../shared/components/loaders/page-loader';
import { fetchProductaddDetails } from './product-api';
import { useTransactionCertificateStore } from '../../../../../store/transaction-certificates.store';
import { isViewOnlyAdminMode } from '../../../../../shared/utils/admin-utils';
import { Utills } from '../../../../../shared/app-configs/utilit';
import { TCAppConfig, DELETECONFIG } from '../../../../../shared/app-configs/tc-app-configs';
import useNotification from '../../../../../shared/hooks/useNotification';
import AlertPopup from '../../../../../shared/components/modals/alert-moadal';

export interface ComponentEditRequestContext {
  componentId: string;
  productId: string;
  shipmentId: string;
  productNumber: string;
  shipmentNumber: string;
}

interface RenderDetailPanelProps {
  row: any;
  /** When user clicks Edit on a component row, call this to open the component modal in edit mode */
  onEditComponentRequest?: (ctx: ComponentEditRequestContext) => void;
}

export interface IComponentDetail {
  tcComponentNumber: string;
  tcComponentDetail: string;
  tcComponentNetShippingWeight: number;
  tcComponentSupplementaryWeight: number;
  tcComponentCertifiedWeight: number;
  tcComponentNonCertifiedWeight: number;
  componentId?: string;
}

const formatDynamicDecimals = (value: any) => {
  const numeric = Number(value ?? 0);
  if (Number.isNaN(numeric)) return "0.00";
  
  // For zero, show 0.00
  if (numeric === 0) return "0.00";
  
  // Format with 5 decimals then strip trailing zeros but keep at least 1 decimal
  let str = numeric.toFixed(5);
  str = str.replace(/0+$/, ''); // Remove trailing zeros
  if (str.endsWith('.')) str += '0'; // If ends with dot, add one zero back
  
  return str;
};

const componentColumns = [
  { accessorKey: 'tcComponentNumber', header: 'Component Number', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcComponentDetail', header: 'Component Detail', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcComponentNetShippingWeight', header: 'Component Net Shipping Weight (kg)', minSize: 60, maxSize: 180, Cell: ({ cell }: { cell: any }) => formatDynamicDecimals(cell.getValue()) },
  { accessorKey: 'tcComponentSupplementaryWeight', header: 'Component Supplementary Weight (kg)', minSize: 60, maxSize: 200, Cell: ({ cell }: { cell: any }) => formatDynamicDecimals(cell.getValue()) },
  { accessorKey: 'tcComponentCertifiedWeight', header: 'Component Certified Weight (kg)', minSize: 60, maxSize: 180, Cell: ({ cell }: { cell: any }) => formatDynamicDecimals(cell.getValue()) },
  { accessorKey: 'tcComponentNonCertifiedWeight', header: 'Component Non-Certified Weight (kg)', minSize: 60, maxSize: 200, Cell: ({ cell }: { cell: any }) => formatDynamicDecimals(cell.getValue()) },
];

// Column Definitions
const processorColumns = [
  { accessorKey: 'tcProductLastProcessorName', header: 'Last Processor Name', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductLastProcessorTeId', header: 'Last Processor TE-ID', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductLastProcessorLicenseNo', header: 'Last Processor License No.', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductLastProcessorCountryDesc', header: 'Last Processor Country', minSize: 60, maxSize: 150 },
];

const materialColumns = [
  { accessorKey: 'tcProductInfoFiberLength', header: 'Fiber Length (mm)', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductInfoYarnCount', header: 'Yarn Count', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductInfoFabricWeightAndConstruct', header: 'Fabric Weight and Construct (kg)', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductInfoFabricWeightOfFinishedProduct', header: 'Fabric Weight of Finished Product (kg)', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductInfoTrimsExcluded', header: 'Trim Excluded', minSize: 60, maxSize: 150 },
];

const standardColumns = [
  { accessorKey: 'tcProductStandardLabelGrade', header: 'Standard Label Grade', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductRawMaterialCodeStandardLabel', header: 'Raw Material', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductCertifiedWeight', header: 'Certified Weight (kg)', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductStandardLabelGradeDescription', header: 'Standard Label Grade Description', minSize: 60, maxSize: 150 }
];

const rawMaterialColumns = [
  { accessorKey: 'tcProductRawMaterialCode', header: 'Raw Material Code', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductRawMaterialPercentage', header: 'Raw Material Percentage', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductRawMaterialCodeDescription', header: 'Raw Material Description', minSize: 60, maxSize: 150 },
];

const RenderDetailPanel = memo(({ row, onEditComponentRequest }: RenderDetailPanelProps) => {
  const rowData = row.original;
  const { TE_ID } = useParams();

  const { loading, executeGet, executeDelete } = useApi();
  const [productData, setProductData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [componentData, setComponentData] = useState<IComponentDetail[]>([]);
  const [componentLoading, setComponentLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [componentRowToDelete, setComponentRowToDelete] = useState<any>(null);
  const { notify } = useNotification();
  // Admin is view-only when viewing another CB; edit allowed for own CB
  const isViewMode = isViewOnlyAdminMode();

  const productId = rowData?.productId;
  const shipmentId = rowData?.shipmentId;
  const hasFetchedRef = useRef(false);
  const hasFetchedComponentsRef = useRef(false);

  const onEditComponent = useCallback(
    (componentRow: any) => {
      const compId = componentRow?.original?.componentId;
      if (!productId || !shipmentId || !compId) {
        if (notify) notify('Missing product / shipment / component context for component edit', 'error');
        return;
      }
      const productNumber =
        typeof rowData?.tcProductNo === 'string'
          ? rowData.tcProductNo
          : Array.isArray(rowData?.tcProductNo)
            ? rowData.tcProductNo[0]
            : '';
      const shipmentNumber =
        typeof rowData?.tcAssociatedProductShipmentNo === 'string'
          ? rowData.tcAssociatedProductShipmentNo
          : Array.isArray(rowData?.tcAssociatedProductShipmentNo)
            ? rowData.tcAssociatedProductShipmentNo[0]
            : '';
      if (onEditComponentRequest) {
        onEditComponentRequest({
          componentId: compId,
          productId,
          shipmentId: typeof shipmentId === 'string' ? shipmentId : Array.isArray(shipmentId) ? shipmentId[0] : '',
          productNumber,
          shipmentNumber,
        });
      }
    },
    [productId, shipmentId, rowData, onEditComponentRequest, notify],
  );

  const onDeleteComponent = useCallback((row: any) => {
    setComponentRowToDelete(row);
    setDeleteDialogOpen(true);
  }, []);

  const fetchComponentsByProduct = useCallback(async () => {
    if (!TE_ID || !shipmentId || !productId) return;
    setComponentLoading(true);
    try {
      const res: any = await executeGet(TCAppConfig.TC_COMP_PROD_API, {
        tcId: TE_ID,
        shipmentId,
        productId,
      });
      const list = Array.isArray(res?.data?.response) ? res.data.response : [];
      const mapped: IComponentDetail[] = list.map((item: any) => ({
        tcComponentNumber: item.tcProductComponentNo ?? '',
        tcComponentDetail: item.tcComponentDetail ?? '',
        tcComponentNetShippingWeight: item.tcComponentNetShippingWeight ?? 0,
        tcComponentSupplementaryWeight: item.tcComponentSupplementaryWeight ?? 0,
        tcComponentCertifiedWeight: item.tcComponentCertifiedWeightTotal ?? 0,
        tcComponentNonCertifiedWeight: item.tcComponentNonCertifiedWeightTotal ?? 0,
        componentId: item.componentId,
      }));
      setComponentData(mapped);
    } catch (err: any) {
      setComponentData([]);
      if (notify) notify(err?.message ?? 'Failed to load components', 'error');
    } finally {
      setComponentLoading(false);
    }
  }, [TE_ID, shipmentId, productId, executeGet, notify]);

  const handleConfirmDeleteComponent = useCallback(
    async () => {
      if (!componentRowToDelete) return;

      if (!TE_ID || !productId || !shipmentId) {
        if (notify) {
          notify('Missing TC / product / shipment context for component delete', 'error');
        }
        return;
      }

      const component = componentRowToDelete?.original as IComponentDetail & {
        componentId?: string;
      };
      const compId = component?.componentId ?? '';
      const componentNo = component?.tcComponentNumber ?? '';

      try {
        const res: any = await executeDelete(TCAppConfig.TC_DELETE_API, {
          pageName: DELETECONFIG.component,
          tcId: TE_ID,
          componentId: compId,
          shipmentId,
          productId,
          componentNo: encodeURIComponent(encodeURIComponent(componentNo)),
        });

        if (res?.status === 200) {
          setDeleteDialogOpen(false);
          setComponentRowToDelete(null);
          if (notify) notify('Component deleted successfully', 'success');
          fetchComponentsByProduct();
        } else {
          if (notify) {
            notify('Failed to delete component', 'error');
          }
        }
      } catch (err: any) {
        if (notify) notify(err?.message || 'Failed to delete component', 'error');
      }
    },
    [componentRowToDelete, executeDelete, notify, TE_ID, productId, shipmentId, fetchComponentsByProduct],
  );

  const handleCancelDeleteComponent = useCallback(() => {
    setDeleteDialogOpen(false);
    setComponentRowToDelete(null);
  }, []);

  useEffect(() => {
    if (!productId || !TE_ID || !shipmentId || hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchProductaddDetails(executeGet, TE_ID, productId, shipmentId)
      .then((data) => {
        const store = useTransactionCertificateStore.getState();
        const countryList = Array.isArray(store?.getCountryCodes)
          ? store.getCountryCodes
          : [];

        const tcProductInformationArray = Array.isArray(data?.data?.tcProductInformation)
          ? data.data.tcProductInformation
          : data?.data?.tcProductInformation
          ? [data.data.tcProductInformation]
          : [];

        const enrichedProducts = tcProductInformationArray.map((dat: any) => ({
          ...dat,
          tcProductLastProcessorCountryDesc: dat.tcProductLastProcessorCountry
            ? countryList.find(
                (option: any) => option.value === dat.tcProductLastProcessorCountry,
              )?.label || dat.tcProductLastProcessorCountry || ""
            : "",
        }));

        setProductData(enrichedProducts);
      })
      .catch((err) => { setError(err.message); });
  }, [productId, shipmentId, TE_ID, executeGet]);

  useEffect(() => {
    if (!TE_ID || !shipmentId || !productId || hasFetchedComponentsRef.current) return;
    hasFetchedComponentsRef.current = true;
    fetchComponentsByProduct();
  }, [TE_ID, shipmentId, productId, fetchComponentsByProduct]);

  if (loading) {
    return <LoaderComponent />;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (!productData || productData.length === 0) {
    return <Typography>No product details available.</Typography>;
  }

  // Data Extraction: Since productData is an array
  const product = productData[0]; // Assuming one item per detail panel (adjust if multiple)

  const processorInfo = {
    tcProductLastProcessorName: product.tcProductLastProcessorName,
    tcProductLastProcessorTeId: product.tcProductLastProcessorTeId,
    tcProductLastProcessorLicenseNo: product.tcProductLastProcessorLicenseNo,
    tcProductLastProcessorCountry: product.tcProductLastProcessorCountry,
    tcProductLastProcessorCountryDesc: product.tcProductLastProcessorCountryDesc
  };

  const materialInfo = {
    tcProductInfoFiberLength: product.tcProductInfoFiberLength,
    tcProductInfoYarnCount: product.tcProductInfoYarnCount,
    tcProductInfoFabricWeightAndConstruct: product.tcProductInfoFabricWeightAndConstruct,
    tcProductInfoFabricWeightOfFinishedProduct: product.tcProductInfoFabricWeightOfFinishedProduct,
    tcProductInfoTrimsExcluded: product.tcProductInfoTrimsExcluded,
  };

  const standardInfo = product.tcProductStandardInformation || [];
  const rawMaterialInfo = product.tcProductRawMaterialInformation || [];

  return (
    <>
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 5,
        gap: 4,
        width: '100%',
      }}
    >
      {/* First - Component Information: always call TC_COMP_PROD_API; empty response = empty table, no Edit/Delete */}
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography variant="body2" fontWeight={'400'} sx={{ mb: 1 }}>
          Component Information
        </Typography>
        {componentLoading ? (
          <LoaderComponent />
        ) : (
          <CustomTable
            columns={componentColumns}
            data={componentData}
            enableTopToolbar={false}
            enableRowActions={componentData.length > 0}
            enableBottomToolbar={false}
            renderRowActions={
              componentData.length > 0
                ? ({ row }) => (
                    <ActionsMenu row={row} onEdit={onEditComponent} onDelete={isViewMode ? undefined : onDeleteComponent} />
                  )
                : undefined
            }
          />
        )}
      </Box>
      <Divider />

      {/* Second - Processor Information */}
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography variant="body2" fontWeight={'400'} sx={{ mb: 1 }}>
          Processor Information
        </Typography>
        <CustomTable
          columns={processorColumns}
          data={[processorInfo]}
          enableTopToolbar={false}
          enableRowActions={false}
          enableBottomToolbar={false}
        />
      </Box>
      <Divider />

      {/* Fabric & Material Details */}
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography variant="body2" fontWeight={'400'} sx={{ mb: 1 }}>
          Fabric and Material Details
        </Typography>
        <CustomTable
          columns={materialColumns}
          data={[materialInfo]}
          enableTopToolbar={false}
          enableRowActions={false}
          enableBottomToolbar={false}
        />
      </Box>
      <Divider />

      {/* Standard Details */}
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography variant="body2" fontWeight={'400'} sx={{ mb: 1 }}>
          Standard Details
        </Typography>
        <CustomTable
          columns={standardColumns}
          data={standardInfo}
          enableTopToolbar={false}
          enableRowActions={false}
          enableBottomToolbar={false}
        />
      </Box>
      <Divider />

      {/* Raw Material Composition */}
      <Box sx={{ width: '100%', textAlign: 'left' }} display="grid">
        <Typography variant="body2" fontWeight={'400'} sx={{ mb: 1 }}>
          Raw Material Composition
        </Typography>
        <CustomTable
          columns={rawMaterialColumns}
          data={rawMaterialInfo}
          enableTopToolbar={false}
          enableRowActions={false}
          enableBottomToolbar={false}
        />
      </Box>
    </Box>
    <AlertPopup
      open={deleteDialogOpen}
      title="Confirm Delete ?"
      message={
        componentRowToDelete?.original?.tcComponentNumber
          ? `Are you sure you want to delete component ${componentRowToDelete.original.tcComponentNumber}?`
          : "Are you sure you want to delete this component?"
      }
      onClose={handleCancelDeleteComponent}
      onConfirm={handleConfirmDeleteComponent}
      confirmText="Yes"
      cancelText="Cancel"
    />
    </>
  );
});

export default RenderDetailPanel;
